import React, { useEffect, useMemo, useState } from "react";
import {
  FaAlignLeft,
  FaListOl,
  FaPaintBrush,
  FaSave,
  FaTimes,
  FaUsers,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const GROUP_COLOR_OPTIONS = [
  { value: "bg-indigo-500", label: "Indigo" },
  { value: "bg-purple-500", label: "Violet" },
  { value: "bg-green-500", label: "Vert" },
  { value: "bg-yellow-500", label: "Jaune" },
  { value: "bg-blue-500", label: "Bleu" },
  { value: "bg-red-500", label: "Rouge" },
  { value: "bg-pink-500", label: "Rose" },
  { value: "bg-gray-500", label: "Gris" },
  { value: "bg-cyan-500", label: "Cyan" },
  { value: "bg-orange-500", label: "Orange" },
];

const GroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  group,
  dojoId,
  dojoName,
  demoMode = false,
  isSubmitting = false,
}) => {
  const [nomGroupe, setNomGroupe] = useState("");
  const [descriptionGroupe, setDescriptionGroupe] = useState("");
  const [ordreGroupe, setOrdreGroupe] = useState("");
  const [colorGroupe, setColorGroupe] = useState("bg-gray-500");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (group) {
      setNomGroupe(group.nom_groupe || "");
      setDescriptionGroupe(group.description_groupe || "");
      setOrdreGroupe(group.ordre_groupe || "");
      setColorGroupe(group.color_groupe || "bg-gray-500");
    } else {
      setNomGroupe("");
      setDescriptionGroupe("");
      setOrdreGroupe("");
      setColorGroupe("bg-gray-500");
    }
    setErrorMessage("");
  }, [group, isOpen]);

  const title = useMemo(() => {
    if (group) return demoMode ? "Consulter le groupe" : "Modifier le groupe";
    return "Créer un groupe";
  }, [group, demoMode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (demoMode) {
      setErrorMessage(
        "Mode Démo : les modifications ne peuvent pas être enregistrées."
      );
      return;
    }

    if (!dojoId) {
      setErrorMessage("Aucun lieu sélectionné.");
      return;
    }

    if (!nomGroupe.trim()) {
      setErrorMessage("Le nom du groupe est obligatoire.");
      return;
    }

    const groupData = {
      dojo_id: dojoId,
      nom_groupe: nomGroupe.trim(),
      description_groupe: descriptionGroupe.trim() || "",
      ordre_groupe: ordreGroupe || 0,
      color_groupe: colorGroupe,
    };

    onSubmit(groupData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-6 text-white sm:p-8">
            <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              Groupe
            </p>

            <h2 className="mt-4 text-3xl font-black leading-tight">{title}</h2>
            <p className="mt-3 text-sm leading-7 text-white/80">
              Structurez vos séances avec des groupes clairs et colorés.
            </p>

            <div className="mt-8 rounded-[24px] border border-white/10 bg-white/10 p-5">
              <p className="text-xs uppercase tracking-wider text-white/70">
                Lieu concerné
              </p>
              <h3 className="mt-2 text-xl font-bold">
                {dojoName || "Lieu sélectionné"}
              </h3>

              <div className="mt-5">
                <span
                  className={cn(
                    "inline-flex rounded-full px-4 py-2 text-sm font-bold text-white",
                    colorGroupe
                  )}
                >
                  {nomGroupe || "Nom du groupe"}
                </span>

                <p className="mt-3 text-sm text-white/75">
                  {descriptionGroupe || "Description du groupe"}
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  Formulaire
                </p>
                <h3 className="text-xl font-black text-slate-900 dark:text-slate-100">
                  Paramètres du groupe
                </h3>
              </div>

              <button
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>

            {errorMessage && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Nom du groupe
                </label>
                <div className="relative">
                  <FaUsers className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    value={nomGroupe}
                    onChange={(e) => setNomGroupe(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Ex : Adultes avancés"
                    disabled={demoMode || isSubmitting}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Description
                </label>
                <div className="relative">
                  <FaAlignLeft className="pointer-events-none absolute left-4 top-4 text-slate-400 dark:text-slate-500" />
                  <textarea
                    value={descriptionGroupe}
                    onChange={(e) => setDescriptionGroupe(e.target.value)}
                    className="min-h-[110px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pl-11 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Description facultative"
                    disabled={demoMode || isSubmitting}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                    Ordre
                  </label>
                  <div className="relative">
                    <FaListOl className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                    <input
                      type="number"
                      value={ordreGroupe}
                      onChange={(e) => setOrdreGroupe(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                      placeholder="0"
                      disabled={demoMode || isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                    Couleur
                  </label>
                  <div className="relative">
                    <FaPaintBrush className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                    <select
                      value={colorGroupe}
                      onChange={(e) => setColorGroupe(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                      disabled={demoMode || isSubmitting}
                    >
                      {GROUP_COLOR_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                {GROUP_COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setColorGroupe(option.value)}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition",
                      colorGroupe === option.value
                        ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900"
                        : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    )}
                    disabled={demoMode || isSubmitting}
                  >
                    <span className={cn("h-3 w-3 rounded-full", option.value)} />
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Fermer
                </button>

                <button
                  type="submit"
                  disabled={demoMode || isSubmitting}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                    demoMode || isSubmitting
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                  )}
                >
                  <FaSave />
                  {group ? "Enregistrer" : "Créer le groupe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupModal;